
import './LookupTable.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';

import moment from 'moment';
import { Checkbox } from '@mui/material';
import { fromJS, Map as IMap } from 'immutable';
import InfoIcon from '@mui/icons-material/InfoSharp';
import DeleteIcon from '@mui/icons-material/DeleteSharp';
import InviteUserIcon from '@mui/icons-material/AddCircleSharp';
import KeyIcon from '@mui/icons-material/VpnKeySharp';

import DataTable from './UI/DataTable';
import Button from 'src/component/UI/Button';
import { formatName, isPasswordSecure } from 'src/utils/utils';
import yup from 'src/utils/yup';
import EditableCell from './UI/EditableCell';
import PermissionFence from './PermissionFence';
import InviteUserModal from './InviteUserModal';
import SettingsModal from './UI/SettingsModal/SettingsModal';
import variables from 'src/style/variable/variables.module.scss';
import { clearSetUserPasswordState, fetchUsers, inviteUser, removeUser, setUserPassword, updateUserRole, updateUserSupportState } from 'src/module/user/action';
import { getUsers, getSetPasswordLoading, getSetPasswordError } from 'src/module/user/selector';
import { getAuthenticatedUserId, hasPermissions } from 'src/module/authentication/selector';
import DisplayInfoModal from './UI/DisplayInfoModal';
import ConfirmModal from './UI/ConfirmModal';
import { useTranslation } from 'src/i18n';




export default function UsersList (this: any, props) {
  const { t } = useTranslation();
  const { client, showSupportUsers } = props;
  const dispatch = useDispatch();
  const [inviteUserModalOpen, setInviteUserModalOpen] = useState(false);
  const users = useSelector(getUsers);
  const isAdmin: boolean = useSelector(hasPermissions('smarttrace-admin'));
  const currentUserId = useSelector(getAuthenticatedUserId);

  const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);
  const [userIdToRemove, setUserIdToRemove] = useState('');
  const [removeUserMessage, setRemoveUserMessage] = useState('');
  const [pendingInviteEmail, setPendingInviteEmail] = useState<string | null>(null);
  const [credentialsToShow, setCredentialsToShow] = useState<{ email: string; password: string } | null>(null);
  const [setPasswordModalOpen, setSetPasswordModalOpen] = useState(false);
  const [userToSetPassword, setUserToSetPassword] = useState<{ id: string; name: string } | null>(null);
  const setPasswordLoading = useSelector(getSetPasswordLoading);
  const setPasswordError = useSelector(getSetPasswordError);
  const wasSetPasswordLoadingRef = useRef(false);
  const clientId = client?.get('id') ?? null;
  const prevClientIdRef = useRef(clientId);

  useEffect(() => {
    dispatch(clearSetUserPasswordState());
  }, []);

  useEffect(() => {
    if (!pendingInviteEmail || !users) return;
    const invited = users.find((u) => u.get('email') === pendingInviteEmail && u.get('password'));
    if (invited) {
      setCredentialsToShow({
        email: invited.get('email'),
        password: invited.get('password'),
      });
      setPendingInviteEmail(null);
    }
  }, [users, pendingInviteEmail]);

  const roleLabels = {
    admin: t('users.administrator'),
    eht_specialist: t('users.ehtSpecialist'),
    maintenance_user: t('users.maintenanceUser'),
    operations: t('users.operations'),
    regular_user: t('users.regularUser')
  };

  const _columns: any = [
    {
      id: 'name',
      accessor: user => formatName(user.get('first_name', null), user.get('last_name', null)),
      Header: t('common.name'),
    },
    {
      id: 'email',
      accessor: user => user.get('email'),
      Header: t('users.email'),
    },
    {
      id: 'createdAt',
      accessor: user => moment(user.get('created_at')).format('YYYY-MM-DD'),
      Header: t('users.joined'),
    },
    {
      id: 'lastActivityAt',
      accessor: user => user.get('last_activity_at') ? moment(user.get('last_activity_at')).fromNow() : t('common.never'),
      Header: t('users.lastActivity'),
    },
    {
      id: 'status',
      accessor: user => user.get('status'),
      Header: t('common.status'),
    },
    {
      id: 'roles',
      accessor: user => formatRoles(user.get('roles')),
      Header: (
        <>{t('users.role')}
          <NavLink exact to='/settings/permissions'>
            <InfoIcon className="alert-icon" width={16} height={16} style={{ marginLeft: '5px', marginBottom: '-3px', cursor: 'pointer', color: variables.black }} />
          </NavLink>
        </>
      ),
      disableSortBy: true,
      Cell: (row: any) => {
        if (currentUserId !== row.cell.row.original.get('id') && clientId !== null) {
          return (<EditableCell
            permission='edit-user'
            className='alarm-cell'
            onChange={handleUpdateRole(row.cell.row.original.get('id'))}
            value={row.cell.value || ''}
            emptyCellLabel={t('users.assignUserRole')}
            options={[
              { value: 'admin', label: roleLabels['admin'] },
              { value: 'eht_specialist', label: roleLabels['eht_specialist'] },
              { value: 'maintenance_user', label: roleLabels['maintenance_user'] },
              { value: 'operations', label: roleLabels['operations'] },
              { value: 'regular_user', label: roleLabels['regular_user'] }
            ]}
          >{roleLabels[row.cell.value]}</EditableCell>);
        } else {
          return (roleLabels[row.cell.value]);
        }
      }
    }
  ];

  const toggleInviteUserModal = () => {
    setInviteUserModalOpen(!inviteUserModalOpen);
  };

  useEffect(() => {
    if (clientId !== prevClientIdRef.current) {
      isAdmin ? dispatch(fetchUsers(clientId)) : dispatch(fetchUsers());
      prevClientIdRef.current = clientId;
    }
    else if (clientId === prevClientIdRef.current) {
      isAdmin ? dispatch(fetchUsers(clientId)) : dispatch(fetchUsers());
    }
  }, [clientId]);

  const columns = useMemo(() => {
    if (!users || users.size === 0) return _columns;

    const supportMap = users.reduce((acc, curr) => {
      acc[curr.get('id')] = curr.get('is_support_user', false);
      return acc;
    }, {});

    const dynamicColumns = [..._columns];

    if (isAdmin && showSupportUsers) {
      dynamicColumns.push({
        id: 'support',
        Header: (<div style={{ maxWidth: '20px' }}>{t('users.support')}</div>),
        compact: true,
        width: '60px',
        Cell: (row: any) => (
          <Checkbox
            style={{ maxWidth: '20px' }}
            onChange={() => handleSupportUserClicked(row.cell.row.original.get('id'))}
            checked={supportMap[row.cell.row.original.get('id')] ?? false}
            disabled={!client}
          />
        )
      });
    }

    dynamicColumns.push({
      id: 'actions',
      name: '',
      compact: true,
      width: '90px',
      Cell: (row: any) => (
        currentUserId !== row.cell.row.original.get('id') && (
          <div className='client-user-actions'>
            <PermissionFence can='set-user-password'>
              <Button
                onClick={openSetPasswordModalFor(row.cell.row.original)}
                icon={<KeyIcon style={{ fontSize: 14 }} />}
              />
            </PermissionFence>
            <PermissionFence can='edit-user'>
              <Button onClick={makeToggleRemoveUserModal(row.cell.row.original.get('id'))} icon={<DeleteIcon style={{ fontSize: 14 }} />} />
            </PermissionFence>
          </div>
        )
      )
    });

    return dynamicColumns;
  }, [users, currentUserId, client, isAdmin]);


  const formatRoles = (roles) => {
    const validRoles = [];
    if (!roles) {
      return '';
    }

    roles.forEach((role) => {
      if (roleLabels[role]) {
        validRoles.push(role);
      }
    });

    return validRoles[0];
  };

  const handleUpdateRole = (userId: string) => {
    return (role) => {
      dispatch(updateUserRole(userId, role));
    };
  };

  const handleInviteUser = (values) => {
    dispatch(inviteUser(values.email, values.firstName, values.lastName, clientId));
    setPendingInviteEmail(values.email);
    toggleInviteUserModal();
  };

  const handleRemoveUser = () => {
    dispatch(removeUser(userIdToRemove));
    toggleRemoveUserModal();
  };

  const toggleRemoveUserModal = (userId: string = '') => {
    let name = 'user';

    if (userId) {
      const index = users.findIndex((user) => user.get('id') === userId);

      if (index !== -1) {
        name = users.getIn([index, 'first_name']) || users.getIn([index, 'email']);
      }
    }

    setRemoveUserModalOpen(!removeUserModalOpen);
    setUserIdToRemove(userId);
    setRemoveUserMessage(`Are you sure you would like to remove ${name} from ${client?.get('name') ? 'the ' + client.get('name') + ' facility?' : 'all facilities?'}`);
  };

  const makeToggleRemoveUserModal = (userId: string = '') => {
    return () => {
      toggleRemoveUserModal(userId);
    };
  };

  const openSetPasswordModalFor = (userRec) => () => {
    const name = formatName(userRec.get('first_name', null), userRec.get('last_name', null)) || userRec.get('email');
    setUserToSetPassword({ id: userRec.get('id'), name });
    setSetPasswordModalOpen(true);
  };

  const closeSetPasswordModal = () => {
    setSetPasswordModalOpen(false);
    setUserToSetPassword(null);
  };

  const handleSetPasswordSubmit = (rec) => {
    if (!userToSetPassword) return;
    dispatch(setUserPassword(userToSetPassword.id, rec.get('password')));
  };

  useEffect(() => {
    if (wasSetPasswordLoadingRef.current && !setPasswordLoading && !setPasswordError) {
      closeSetPasswordModal();
    }
    wasSetPasswordLoadingRef.current = setPasswordLoading;
  }, [setPasswordLoading, setPasswordError]);

  const setPasswordFields = useMemo(() => fromJS({
    password: {
      type: 'password',
      label: t('users.password'),
      order: 10,
      schema: {
        required: t('users.passwordRequired'),
        test: ['password-secure', t('users.setPasswordWeak'), isPasswordSecure],
      },
    },
    confirmPassword: {
      type: 'password',
      label: t('users.confirmPassword'),
      order: 20,
      schema: {
        required: t('users.confirmPasswordRequired'),
        oneOf: { value: [yup.ref('password'), null], message: t('users.passwordsMustMatch') },
      },
    },
  }), [t]);


  const handleSupportUserClicked = useCallback((userId: string) => {
    const currentState = users.find(user => user.get('id') === userId)?.get('is_support_user', false);
    dispatch(updateUserSupportState(userId, client?.get('id'), !currentState));
  }, [dispatch, users, client]);

  return (
    <div id="users-table" className='users-list'>
      <InviteUserModal open={inviteUserModalOpen} handleClose={toggleInviteUserModal} handleSubmit={handleInviteUser} />
      <DisplayInfoModal
        open={!!credentialsToShow}
        title={t('users.userCredentials')}
        handleClose={() => setCredentialsToShow(null)}
        data={credentialsToShow ?? {}}
      />
      <ConfirmModal title={t('modal.areYouSure')} message={removeUserMessage} open={removeUserModalOpen} handleClose={makeToggleRemoveUserModal()} handleReject={makeToggleRemoveUserModal()} handleConfirm={handleRemoveUser} />
      <SettingsModal
        open={setPasswordModalOpen}
        title={userToSetPassword
          ? t('users.setPasswordModalTitle', { name: userToSetPassword.name })
          : t('users.setPassword')}
        handleClose={closeSetPasswordModal}
        handleSubmit={handleSetPasswordSubmit}
        handleChange={() => { /* no field-driven side effects */ }}
        fields={setPasswordFields}
        settings={IMap({ password: '', confirmPassword: '' })}
        loading={setPasswordLoading}
      />
      <DataTable
        columns={columns}
        data={users}
        tableId='clientScreenTable'
      />
      <PermissionFence can='edit-user'>
        <div className='button-bar'>
          <Button disabled={!client} className="invite-user-btn" onClick={toggleInviteUserModal} icon={<InviteUserIcon style={{ fontSize: 14 }} />} cta >{t('users.inviteUser')}</Button>
        </div>
      </PermissionFence>
    </div>
  );
}

UsersList.propTypes = {
  type: PropTypes.string,
  client: PropTypes.object,
  showSupportUsers: PropTypes.bool
};

UsersList.defaultProps = {
  showSupportUsers: false
};
